import { appConfig, isLocal, isProd } from '@/constants/appConfig'
import type { ApiError } from '@/types/api'
import { APP_ROUTES } from '@/routes/routes.constants'
import { AUTH_STORAGE_KEYS } from '@/stores/authStoreConstants'
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

export type { ApiError } from '@/types/api'

declare module 'axios' {
  interface AxiosRequestConfig {
    // Brand checks show their own no-access page, so a 403 must not redirect away.
    skipForbiddenRedirect?: boolean
  }
}

const BASE_CONFIG: AxiosRequestConfig = {
  baseURL: appConfig.baseUrl,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 1000 * 60 * 5,
}

class ApiClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create(BASE_CONFIG)
    this.setupInterceptors()
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
        if (token) {
          config.headers.set('access_token', token)
        }

        if (isLocal) {
          const userId = localStorage.getItem(AUTH_STORAGE_KEYS.USER_ID)
          if (userId) {
            config.headers.set('x-gw-social_ads-uid', userId)
          }
        }

        config.headers.set('app_client', appConfig.appClient)
        const authKey = isProd ? appConfig.authApiKeyProd : appConfig.authApiKey
        config.headers.set('x-api-key', authKey)

        return config
      },
      (error: AxiosError) => Promise.reject(error)
    )

    // Response interceptor for handling errors and token validation
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        const status = error.response?.status

        switch (status) {
          case 401:
            this.handleLogout(error)
            break
          case 403:
            this.handleForbidden(error)
            break
          default:
            // Log all other API errors for debugging
            console.error('API Error:', {
              message: error.message,
              status,
              url: error.config?.url,
              method: error.config?.method,
              error,
            })
            break
        }

        return Promise.reject(this.transformError(error))
      }
    )
  }

  private handleLogout(error: AxiosError) {
    // Clear all auth data from localStorage
    const requestUrl = error.config?.url || ''
    const isOtpEndpoint =
      requestUrl.includes('/auth/api/v1/email/send_otp') ||
      requestUrl.includes('/auth/api/v1/email/verify_otp')

    if (isOtpEndpoint) {
      return
    }
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER_ID)
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER)

    // Redirect to login page immediately
    window.location.href = APP_ROUTES.HOME
  }

  private handleForbidden(error: AxiosError) {
    if (error.config?.skipForbiddenRedirect) return
    // Don't redirect for OTP endpoints - they're part of the authentication flow
    const requestUrl = error.config?.url || ''
    const isOtpEndpoint =
      requestUrl.includes('/auth/api/v1/email/send_otp') ||
      requestUrl.includes('/auth/api/v1/email/verify_otp')

    if (isOtpEndpoint) {
      return
    }

    const currentPath = window.location.pathname
    if (currentPath !== APP_ROUTES.SELECT_PLATFORM) {
      window.location.href = APP_ROUTES.SELECT_PLATFORM
    }
  }

  private transformError(error: unknown): ApiError {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data
      const message =
        typeof responseData === 'object' && responseData !== null
          ? (responseData as { message?: string }).message || error.message
          : error.message

      let code: string | undefined
      if (typeof responseData === 'object' && responseData !== null && 'code' in responseData) {
        const raw = (responseData as { code?: number | string }).code
        if (raw !== undefined && raw !== null) {
          code = String(raw)
        }
      }

      const httpStatus = error.response?.status
      return {
        message,
        status: httpStatus !== undefined ? String(httpStatus) : undefined,
        code,
        details:
          typeof responseData === 'object' && responseData !== null
            ? (responseData as { details?: unknown }).details
            : undefined,
      }
    }
    return { message: 'An unexpected error occurred' }
  }

  async get<T>(url: string, config?: AxiosRequestConfig) {
    const response: AxiosResponse<T> = await this.instance.get(url, config)
    return response.data
  }

  async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    const response: AxiosResponse<T> = await this.instance.post(url, data, config)
    return response.data
  }

  async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    const response: AxiosResponse<T> = await this.instance.put(url, data, config)
    return response.data
  }

  async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig) {
    const response: AxiosResponse<T> = await this.instance.patch(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig) {
    const response: AxiosResponse<T> = await this.instance.delete(url, config)
    return response.data
  }
}

export const apiClient = new ApiClient()
export default apiClient
